/**
 * Repartir una dirección que llegó entera en una sola casilla.
 *
 * De dónde sale (Javier Volcanes, 2026-08-28): *"cuando quiere filtrar por
 * estado solo le sale TX"*. El desplegable de Estado en Clientes se arma con
 * los valores distintos de la columna `state`, y esa columna estaba vacía en
 * casi toda su cartera.
 *
 * **OJO: partir la dirección NO fue lo que arregló ese caso.** Fue la primera
 * hipótesis y era falsa — sus fichas traen calle, ciudad, ZIP y condado bien
 * puestos y sólo les falta el estado, que sale del **código postal** (ver
 * `repartirDireccionEnFicha`, más abajo). Esto cubre el otro caso, el del
 * tablero que trae la dirección entera en una casilla, que también existe.
 *
 * Lo usan los DOS importadores (`components/ImportView.jsx` para CSV/Excel y
 * `mondayImport.js` para la migración de monday). Vive aquí, y no dentro de
 * uno de ellos, para que no haya dos versiones que se separen con el tiempo.
 *
 * El backend tiene su propia copia en `deskte-api/src/utils/addressParts.ts`
 * (frontend y backend se despliegan por separado y no comparten módulos). La
 * de allí es MÁS conservadora a propósito: repasa fichas ya guardadas sin que
 * nadie mire, mientras que ésta alimenta una vista previa que el usuario
 * revisa antes de importar. Si se toca el criterio, se tocan las dos.
 */

import { US_STATE_CODES, normalizarEstado, estadoDesdeZip } from './estadosUsa.js';

/**
 * Sufijos de calle que también son códigos de estado. "1234 Main St NE" no
 * vive en Nebraska: solo cuentan como estado si los respalda una coma delante
 * o un código postal detrás.
 */
const DIRECCIONALES = new Set(['NE', 'NW', 'SE', 'SW']);

/**
 * Palabras que rematan una calle. Sin esta lista, "123 Farnam St NE 68102"
 * salía con la ciudad **"St"**: cuando no queda ninguna coma, la última
 * palabra se toma como ciudad, y ahí la última palabra era el tipo de vía.
 */
const SUFIJOS_DE_CALLE = new Set([
  'ST', 'STREET', 'AVE', 'AVENUE', 'RD', 'ROAD', 'BLVD', 'BOULEVARD',
  'DR', 'DRIVE', 'LN', 'LANE', 'CT', 'COURT', 'PL', 'PLACE', 'WAY',
  'TER', 'TERRACE', 'CIR', 'CIRCLE', 'PKWY', 'PARKWAY', 'HWY', 'HIGHWAY',
  'TRL', 'TRAIL', 'PLZ', 'PLAZA', 'SQ', 'LOOP', 'RUN', 'PASS', 'PATH',
  'APT', 'STE', 'SUITE', 'UNIT', 'BLDG', 'FL', 'RM',
]);

/**
 * Nombres largos de estado al final de la línea ("…, Orlando, Florida 32819").
 * Devuelve `{ resto, codigo }` o `null`.
 *
 * Un nombre largo suelto NO basta: "1234 Virginia" es una calle. Hace falta
 * que lo respalde una coma justo delante, un código postal detrás, o alguna
 * coma en lo que queda (o sea: la línea viene troceada y esto es el último
 * trozo). Mismo criterio que `deskte-api/src/utils/addressParts.ts`.
 */
function nombreLargoAlFinal(s, hayZip) {
  const palabras = s.split(/\s+/);
  for (let n = Math.min(4, palabras.length); n >= 1; n--) {
    const cola = palabras.slice(palabras.length - n).join(' ');
    const codigo = normalizarEstado(cola);
    // Solo nombres LARGOS por esta vía: los de dos letras los ve la otra rama,
    // que además sabe distinguir un sufijo de calle.
    if (!codigo || cola.replace(/[.\s]/g, '').length <= 2) continue;
    const cabeza = palabras.slice(0, palabras.length - n).join(' ');
    const resto = cabeza.replace(/[\s,]+$/, '');
    if (!/,\s*$/.test(cabeza) && !hayZip && !resto.includes(',')) return null;
    // "Washington" es también una ciudad muy común: sin nada delante se deja
    // para la ciudad, que es lo que casi siempre es.
    if (codigo === 'WA' && !resto) return null;
    return { resto, codigo };
  }
  return null;
}

/**
 * @param {string} calle  la casilla de dirección tal cual vino del archivo
 * @param {string} ciudad la casilla de ciudad (si el archivo trae una)
 * @param {string} estado la casilla de estado (si el archivo trae una)
 * @returns {{street:string, city:string, state:string, zip:string}}
 *   `state` y `zip` son SOLO lo RECUPERADO de dentro de la calle: vienen
 *   vacíos cuando no había nada que sacar. El llamante rellena únicamente sus
 *   casillas vacías — lo que trae el archivo manda siempre.
 *
 * Sin un estado reconocible al final NO se parte nada: se devuelve la calle
 * intacta. Sin esa ancla no hay forma honesta de saber dónde acaba la calle,
 * y una ciudad inventada no se puede deshacer.
 */
export function partirDireccion(calle, ciudad, estado) {
  const st = String(calle || '').trim();
  const ciudadExistente = String(ciudad || '').trim();
  const intacto = { street: st, city: ciudadExistente, state: '', zip: '' };
  if (!st) return intacto;

  // 1) Código postal al final.
  let zip = '';
  let s = st;
  const zm = s.match(/[,\s]+(\d{5}(?:-\d{4})?)\s*$/);
  if (zm) { zip = zm[1]; s = s.slice(0, zm.index).trim(); }

  // 2) Estado al final. Primero el que ya trae el archivo — así se recorta la
  //    repetición aunque venga escrito de otra forma ("Florida" en su casilla
  //    y "FL" al final de la calle) — y si no, cualquier código real.
  const suyo = normalizarEstado(estado);
  let hadState = false;
  let recuperado = '';
  if (suyo) {
    const re = new RegExp('[,\\s]+' + suyo + '\\s*$', 'i');
    if (re.test(s)) { s = s.replace(re, '').trim(); hadState = true; }
  }
  // 2b) El nombre largo ("…, Orlando, Florida 32819"). Va antes que el código
  //     de dos letras porque "Washington DC" tiene que ganarle a "DC" suelto.
  if (!hadState) {
    const largo = nombreLargoAlFinal(s, !!zip);
    if (largo) { s = largo.resto; hadState = true; recuperado = largo.codigo; }
  }
  // 2c) Código de dos letras al final.
  if (!hadState) {
    const mm = s.match(/(^|[,\s])([A-Za-z]{2})\s*$/);
    const code = mm ? mm[2].toUpperCase() : '';
    const respaldado = !DIRECCIONALES.has(code) || mm[1] === ',' || !!zip;
    if (code && US_STATE_CODES.has(code) && respaldado) {
      s = s.slice(0, mm.index + mm[1].length).replace(/[\s,]+$/, '');
      hadState = true;
      recuperado = code;
    }
  }
  // Sin estado no hay ancla: se devuelve la calle tal cual, con su ZIP dentro
  // (que es donde estaba), para no mutilarla.
  if (!hadState) return intacto;

  // 3) Ciudad.
  let street = s;
  let city = ciudadExistente;
  if (ciudadExistente) {
    // El archivo ya trae ciudad: no se toca, pero si la calle la repite al
    // final se recorta, o la ficha acaba enseñando "…Rd, Sarasota, Sarasota".
    const re = new RegExp('[,\\s]+' + ciudadExistente.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i');
    if (re.test(street)) street = street.replace(re, '').trim();
  } else {
    const ci = s.lastIndexOf(',');
    if (ci >= 0 && s.slice(ci + 1).trim()) {
      city = s.slice(ci + 1).trim();
      street = s.slice(0, ci).trim();
    } else {
      // Sin coma solo nos fiamos si quedan >=3 palabras Y la última parece un
      // nombre de sitio: sin ese filtro, "123 Farnam St NE 68102" dejaba la
      // ciudad en **"St"** (Javier Volcanes, 2026-08-28).
      const partes = s.split(/\s+/);
      const ultima = partes[partes.length - 1] || '';
      const pareceCiudad = ultima.length >= 3
        && !/\d/.test(ultima)
        && !SUFIJOS_DE_CALLE.has(ultima.toUpperCase());
      if (partes.length >= 3 && pareceCiudad) { city = partes.pop(); street = partes.join(' '); }
    }
  }
  return { street: street.replace(/[\s,]+$/, ''), city, state: recuperado, zip };
}

/**
 * Rellenar las casillas VACÍAS de una ficha con lo que venga dentro de su
 * dirección. Muta y devuelve el mismo objeto, que es como lo usan los dos
 * importadores.
 *
 * `claves` permite decir cómo se llaman los campos en cada importador
 * (`addressStreet`/`addressCity`/… en el CSV, `address`/`city`/… en monday).
 */
export function repartirDireccionEnFicha(ficha, claves) {
  const { calle, ciudad, estado, zip } = claves;
  if (ficha[ciudad] && ficha[estado] && ficha[zip]) return ficha;

  if (ficha[calle]) {
    const sp = partirDireccion(ficha[calle], ficha[ciudad], ficha[estado]);
    ficha[calle] = sp.street;
    if (sp.city && !ficha[ciudad]) ficha[ciudad] = sp.city;
    if (sp.state && !ficha[estado]) ficha[estado] = sp.state;
    if (sp.zip && !ficha[zip]) ficha[zip] = sp.zip;
  }

  // Si después de todo sigue sin estado pero HAY código postal, el ZIP lo
  // determina. Éste es el caso que de verdad se da (Javier Volcanes,
  // 2026-08-28): el archivo trae Dirección, Ciudad, ZIP y Condado, y no trae
  // columna de Estado — así que en la calle no hay nada que partir, pero en
  // el 34772 sí. Sin red: la tabla va dentro (ver `estadosUsa.js`).
  if (!ficha[estado]) {
    const porZip = estadoDesdeZip(ficha[zip]);
    if (porZip) ficha[estado] = porZip;
  }
  return ficha;
}

/**
 * La dirección escrita en UN SOLO RENGLÓN (la del médico) como la pregunta del
 * corrector de direcciones (Lissette 2026-10-23: *"el mismo sistema que tenemos
 * ahorita colócalo también cuando estemos colocando la dirección de los
 * doctores en citas médicas"*). El corrector (US Census) necesita número + calle
 * y además ZIP o ciudad; `partirDireccion` solo parte si encuentra el estado,
 * así que si no lo hay se saca al menos el ZIP del final.
 */
export function consultaDeUnaLinea(texto) {
  const linea = String(texto || '').trim();
  const p = partirDireccion(linea, '', '');
  if (p.zip || p.city) return { street: p.street, city: p.city, state: p.state, zip: p.zip };
  const zm = linea.match(/[,\s]+(\d{5})(?:-\d{4})?\s*$/);
  if (zm) return { street: linea.slice(0, zm.index).replace(/[\s,]+$/, ''), city: '', state: '', zip: zm[1] };
  return { street: linea, city: '', state: '', zip: '' };
}
